import React, { useState } from 'react';
import { Users, LayoutGrid, Trash2, ArrowRight, GitBranch, ChevronRight } from 'lucide-react';
import MemberAvatar from '../ui/MemberAvatar';
import TeamMemberRowMenu from '../TeamMemberRowMenu';
import { DARK, memberCountDisplay } from './teamsTheme';
import { HubRoleBadge, TableRoleBadge, TeamAvatar } from './TeamsBadges';
import { ago } from './teamsActivity';
import { AccentBtn, DropdownItem, OutlineBtn } from './TeamsControls';

// ─── TeamCard ─────────────────────────────────────────────────────────────────
export function TeamCard({ team, role, pending, lastActive, onClick, T }) {
  const [hov, setHov] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        width:'100%', borderRadius:12,
        border:`1px solid ${hov ? T.chipBorder : T.border}`,
        background: hov ? T.cardHover : T.card,
        padding:'14px 16px', textAlign:'left', cursor:'pointer', fontFamily:'inherit',
      }}>
      <div style={{display:'flex', alignItems:'center', gap:14}}>
        <TeamAvatar team={team} size={40}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
            <span style={{fontSize:13, fontWeight:600, color:T.text,
              overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{team.name}</span>
            <HubRoleBadge role={role} T={T}/>
          </div>
          <div style={{display:'flex', flexWrap:'wrap', alignItems:'center',
            gap:'2px 12px', fontSize:12, color:T.textMuted}}>
            <span style={{display:'flex', alignItems:'center', gap:4}}>
              <LayoutGrid size={11} style={{opacity:.7}}/> {team.workspaces_count??0} {(team.workspaces_count??0)===1?'workspace':'workspaces'}
            </span>
            <span style={{display:'flex', alignItems:'center', gap:4}}>
              <Users size={11} style={{opacity:.7}}/> {memberCountDisplay(team.members_count??0, team.owner?.plan)}
            </span>
            {pending>0 && <span style={{color:T.amber, fontWeight:500}}>{pending} pending</span>}
            {lastActive && <span>· {lastActive}</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── MemberRow ────────────────────────────────────────────────────────────────
export function MemberRow({ m, mc, isLast, selected, onToggle, canChangeRole, canRemove, onChangeRole, onRemove, menuOpen, onMenuToggle, onMenuClose, T }) {
  const [hov, setHov] = useState(false);
  return (
    <tr onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background: selected ? (T === DARK ? 'rgba(139,92,246,0.07)' : 'rgba(139,92,246,0.04)') : hov ? T.hover : 'transparent',
        borderBottom:isLast?'none':`1px solid ${T.divider}`}}>
      <td style={{padding:'12px 16px'}}>
        <input type="checkbox" className="checkbox-brand flex-shrink-0 cursor-pointer"
          checked={selected} onChange={onToggle}/>
      </td>
      <td style={{padding:'12px 16px'}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          {/* The person's real picture, and the same profile card chat and
              boards show on hover. It was a gradient square with one letter
              in it — which is a colour, not a person, and told you nothing
              about who you were about to remove from a team. */}
          <MemberAvatar
            userId={m.id}
            name={m.name}
            email={m.email}
            size={32}
            rounded="rounded"
          />
          <div>
            <p style={{margin:0, fontSize:13, fontWeight:500, color:T.text}}>{m.name}</p>
            <p style={{margin:0, fontSize:11, color:T.textMuted}}>{m.email}</p>
          </div>
        </div>
      </td>
      <td style={{padding:'12px 16px'}}>
        <TableRoleBadge role={m.role} canChange={canChangeRole} onChange={onChangeRole} T={T}/>
      </td>
      <td style={{padding:'12px 16px', fontSize:12, color:T.textMuted, whiteSpace:'nowrap'}}>
        {m.joined_at ? new Date(m.joined_at).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—'}
      </td>
      <td style={{padding:'12px 16px', fontSize:12, color:T.textMuted, whiteSpace:'nowrap'}}>
        {ago(m.joined_at)||'—'}
      </td>
      <td style={{padding:'12px 16px', textAlign:'right'}}>
        {canRemove && (
          <TeamMemberRowMenu open={menuOpen} onToggle={onMenuToggle} onClose={onMenuClose} T={T}>
            <DropdownItem icon={Trash2} label="Remove from team" danger
              onClick={()=>{ onMenuClose(); onRemove(); }} T={T}/>
          </TeamMemberRowMenu>
        )}
      </td>
    </tr>
  );
}

// ─── LibraryCard ──────────────────────────────────────────────────────────────
export function LibraryCard({ ws, canManage, onOpen, onFork, onRemove, T, t }) {
  const [hov, setHov] = useState(false);
  const forkedFrom = ws.forked_from?.name || ws.forked_from_name || ws.source_workspace?.name;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: 12,
        border: `1px solid ${hov ? T.chipBorder : T.border}`,
        background: T.card,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'rgba(139,92,246,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <LayoutGrid size={14} style={{ color: '#a78bfa' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: T.text,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {ws.name}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: T.textMuted }}>
              {t('teamLibrary.updatedAgo', { time: ago(ws.updated_at) || '—' })}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12 }}>
        <span style={{ fontWeight: 500, color: T.textSub }}>{ws.requests_count ?? 0}</span>
        <span style={{ color: T.textMuted }}>{t('teamLibrary.requests')}</span>
        {forkedFrom && (
          <span
            style={{
              borderRadius: 99,
              background: T.chip,
              padding: '2px 8px',
              color: T.textSub,
              fontSize: 11,
            }}
          >
            {t('teamLibrary.forkedFrom', { name: forkedFrom })}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <AccentBtn onClick={() => onOpen?.(ws)} icon={ArrowRight}>
          {t('teamLibrary.open')}
        </AccentBtn>
        <OutlineBtn onClick={() => onFork?.(ws)} icon={GitBranch} T={T}>
          {t('teamLibrary.fork')}
        </OutlineBtn>
        {canManage && (
          <OutlineBtn onClick={() => onRemove?.(ws)} icon={Trash2} T={T}>
            {t('teamLibrary.remove')}
          </OutlineBtn>
        )}
      </div>
    </div>
  );
}

// A team board summary card (mirrors LibraryCard). `board` comes from
// GET /teams/:id/boards: { id, name, description, color, taskCount, updatedAt,
// shared_by, can_edit }.
export function BoardCard({ board, canManage, onOpen, onRemove, T, t }) {
  const [hov, setHov] = useState(false);
  const count = board.taskCount ?? board.task_count ?? 0;
  const sharedBy = board.shared_by?.name || board.shared_by_name;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: 12,
        border: `1px solid ${hov ? T.chipBorder : T.border}`,
        background: T.card,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: board.color || 'linear-gradient(135deg, #2563EB, #9333EA)',
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: T.text,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {board.name}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: T.textMuted }}>
              {t('teamLibrary.updatedAgo', { time: ago(board.updated_at || board.updatedAt) || '—' })}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12 }}>
        <span style={{ fontWeight: 500, color: T.textSub }}>{count}</span>
        <span style={{ color: T.textMuted }}>{count === 1 ? 'task' : 'tasks'}</span>
        {sharedBy && (
          <span
            style={{
              borderRadius: 99,
              background: T.chip,
              padding: '2px 8px',
              color: T.textSub,
              fontSize: 11,
            }}
          >
            {sharedBy}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <AccentBtn onClick={() => onOpen?.(board)} icon={ArrowRight}>
          {t('teamLibrary.open')}
        </AccentBtn>
        {canManage && (
          <OutlineBtn onClick={() => onRemove?.(board)} icon={Trash2} T={T}>
            {t('teamLibrary.remove')}
          </OutlineBtn>
        )}
      </div>
    </div>
  );
}

// ─── QuickActionRow ───────────────────────────────────────────────────────────
export function QuickActionRow({ Icon, title, sub, onClick, T }) {
  const [hov, setHov] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        display:'flex', alignItems:'center', gap:12, width:'100%',
        borderRadius:8, padding:'8px 10px', textAlign:'left', cursor:'pointer',
        background:hov?T.hover:'transparent', border:'none', fontFamily:'inherit',
      }}>
      <div style={{width:28, height:28, borderRadius:8, background:T.chip,
        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
        <Icon size={13} style={{color:T.textSub}}/>
      </div>
      <div style={{flex:1, minWidth:0}}>
        <p style={{margin:0, fontSize:12, fontWeight:500, color:T.textSub}}>{title}</p>
        <p style={{margin:0, fontSize:11, color:T.textMuted,
          overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{sub}</p>
      </div>
      <ChevronRight size={13} style={{color:T.chipBorder, flexShrink:0}}/>
    </button>
  );
}
